/**
 * 会员管理页面 — 显示所有客户的 VIP 等级、积分余额、消费金额
 */
import { useState, useEffect } from "react";
import { useRedirect } from "ra-core";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { Crown, Star, TrendingUp, Users } from "lucide-react";
import { Input } from "@/components/ui/input";

type MemberRow = {
  id: number;
  first_name: string;
  last_name: string;
  company_name: string | null;
  email: string | null;
  vip_level: string;
  total_spent_usd: number;
  total_spent_eur: number;
  points_balance: number;
  country_code: string | null;
};

const VIP_STYLE: Record<string, { label: string; cls: string; icon: string }> = {
  standard: { label: "注册客户 Registered", icon: "",   cls: "bg-gray-100 text-gray-600 border-gray-200" },
  silver:   { label: "银牌 Silver",         icon: "🥈", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  gold:     { label: "金牌 Gold",           icon: "🥇", cls: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  platinum: { label: "铂金 Platinum",       icon: "💎", cls: "bg-purple-100 text-purple-700 border-purple-200" },
  diamond:  { label: "钻石 Diamond",        icon: "👑", cls: "bg-sky-100 text-sky-700 border-sky-200" },
};

const VIP_TABS = ["all", "diamond", "platinum", "gold", "silver", "standard"] as const;

const MembersPage = () => {
  const redirect = useRedirect();
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<string>("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    getSupabaseClient()
      .from("contacts_summary")
      .select("id, first_name, last_name, company_name, email, vip_level, total_spent_usd, total_spent_eur, country_code")
      .order("total_spent_usd", { ascending: false })
      .limit(200)
      .then(({ data }) => {
        if (data) {
          setMembers(
            (data as MemberRow[]).map((r) => ({
              ...r,
              total_spent_usd: r.total_spent_usd ?? 0,
              total_spent_eur: r.total_spent_eur ?? 0,
              points_balance: 0,
            }))
          );
        }
        setLoading(false);
      });
  }, []);

  const filtered = members.filter((m) => {
    if (tab !== "all" && m.vip_level !== tab) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        `${m.first_name} ${m.last_name}`.toLowerCase().includes(q) ||
        m.company_name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const countByLevel = (level: string) => members.filter((m) => m.vip_level === level).length;

  const totalSpentAll = members.reduce((s, m) => s + m.total_spent_usd, 0);

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold">会员管理</h1>
          {!loading && (
            <span className="text-xs text-muted-foreground ml-1">
              {filtered.length} 位会员
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Stats */}
          <div className="hidden md:flex gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              <span>{members.length} 客户</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>总消费 ${totalSpentAll.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-yellow-500" />
              <span>{countByLevel("gold") + countByLevel("platinum") + countByLevel("diamond")} 高级会员</span>
            </div>
          </div>
          {/* Search */}
          <div className="relative w-56">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索姓名、公司..."
              className="h-8 text-xs"
            />
          </div>
        </div>
      </div>

      {/* VIP Tabs */}
      <div className="flex gap-0 border-b">
        {VIP_TABS.map((t) => {
          const count = t === "all" ? members.length : countByLevel(t);
          const cfg = t === "all" ? null : VIP_STYLE[t];
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                tab === t
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t === "all" ? "全部" : cfg?.label}
              {count > 0 && (
                <span className="ml-1.5 text-xs text-muted-foreground">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Table header */}
      <div className="hidden md:grid grid-cols-[1fr_140px_110px_110px_110px] gap-3 px-4 py-2 text-xs text-muted-foreground font-medium border-b">
        <div>客户 / 公司</div>
        <div>VIP 等级</div>
        <div className="text-right">USD 消费</div>
        <div className="text-right">EUR 消费</div>
        <div>国家</div>
      </div>

      {/* Rows */}
      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">加载中...</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">暂无会员数据</div>
      ) : (
        <div className="space-y-1">
          {filtered.map((m) => {
            const vip = VIP_STYLE[m.vip_level] ?? VIP_STYLE.standard;
            return (
              <div
                key={m.id}
                onClick={() => redirect("show", "contacts", m.id)}
                className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_140px_110px_110px_110px] gap-3 items-center px-4 py-3 bg-card border rounded-lg hover:bg-accent/40 cursor-pointer transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-medium text-sm">
                    {m.first_name} {m.last_name}
                  </div>
                  {m.company_name && (
                    <div className="text-xs text-muted-foreground truncate">{m.company_name}</div>
                  )}
                  {m.email && (
                    <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                  )}
                </div>

                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${vip.cls}`}>
                    {vip.icon} {vip.label}
                  </span>
                </div>

                <div className="hidden md:block text-sm font-medium text-right">
                  {m.total_spent_usd > 0 ? `$${m.total_spent_usd.toLocaleString()}` : "—"}
                </div>

                <div className="hidden md:block text-sm text-right text-muted-foreground">
                  {m.total_spent_eur > 0 ? `€${m.total_spent_eur.toLocaleString()}` : "—"}
                </div>

                <div className="hidden md:block text-xs text-muted-foreground uppercase">
                  {m.country_code ?? "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MembersPage;
